import type { MyTenant, MyTenantsResponse } from '@warehouselogix/shared';
import { createContext, useCallback, useContext, useMemo } from 'react';

import { usePlatformApi } from '../api/usePlatformApi';
import { useAsyncData } from '../components/useAsyncData';

/**
 * `GET /me/tenants`, loaded ONCE per sign-in (tenancy v1, PLAN §5).
 *
 * The shell owns the loader ({@link useMyTenantsLoader}) and publishes it
 * through {@link MyTenantsContext}; everything below (picker, switcher,
 * current-tenant provider, platform-access gate) reads the same answer via
 * {@link useMyTenants}. `reload()` re-asks the API — the current-tenant
 * provider calls it after any 403 so a revoked grant bounces the user to the
 * picker instead of leaving a dead screen.
 */
export interface MyTenants {
  /** The reply, or null before the first successful load. */
  data: MyTenantsResponse | null;
  /** True while a load (initial or reload) is in flight. */
  loading: boolean;
  /** User-safe error line, or null. */
  error: string | null;
  /** Re-fetch `me/tenants`. */
  reload: () => void;
}

export const MyTenantsContext = createContext<MyTenants | null>(null);

/** Shell side: perform the load. The shell mounts once per authenticated session. */
export function useMyTenantsLoader(): MyTenants {
  const api = usePlatformApi();
  const state = useAsyncData<MyTenantsResponse>(useCallback(() => api.myTenants(), [api]));
  const { data, loading, error, reload } = state;
  return useMemo<MyTenants>(
    () => ({ data, loading, error, reload }),
    [data, loading, error, reload]
  );
}

/** Consumer side. Throws outside the shell — that is a wiring bug, not a runtime state. */
export function useMyTenants(): MyTenants {
  const ctx = useContext(MyTenantsContext);
  if (ctx === null) {
    throw new Error('useMyTenants() must be used inside the admin shell (MyTenantsContext).');
  }
  return ctx;
}

// ------------------------------------------------------------ pure helpers

/** sessionStorage key holding the slug of the tenant last opened via /t/:slug. */
export const LAST_TENANT_KEY = 'warehouselogix.admin.lastTenant';

export function readLastTenant(): string | null {
  try {
    return window.sessionStorage.getItem(LAST_TENANT_KEY);
  } catch {
    return null;
  }
}

export function writeLastTenant(slug: string): void {
  try {
    window.sessionStorage.setItem(LAST_TENANT_KEY, slug);
  } catch {
    // Storage unavailable (private mode / quota) — the picker just shows the list.
  }
}

export function findTenantBySlug(data: MyTenantsResponse, slug: string): MyTenant | null {
  return data.tenants.find((t) => t.slug === slug) ?? null;
}

export function findTenantById(data: MyTenantsResponse, id: string): MyTenant | null {
  return data.tenants.find((t) => t.id === id) ?? null;
}

/** The tenant-relative admin path for a slug and screen segment. */
export function tenantPath(slug: string, screen: string): string {
  return `/t/${encodeURIComponent(slug)}/${screen}`;
}
